import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  X,
  CheckCircle,
  AlertCircle,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { Alert, AlertTitle } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { RootState } from '../../reducers';
import {
  setRecoveryAlertClosed,
  setRecoveryAlertMinimized,
} from './settingsSlice';

const RecoveryProgressAlert: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { recoveryState, alertClosed, isRecoveryAlertMinimized } = useSelector(
    (state: RootState) => state.settings.recovery,
  );

  // Find recoveries that are actively ongoing
  const activeRecoveries = Object.entries(recoveryState).filter(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ([_, state]) => state.status === 'ongoing',
  );

  const handleClose = () => {
    dispatch(setRecoveryAlertClosed(true));
  };

  const handleMinimize = () => {
    dispatch(setRecoveryAlertMinimized(!isRecoveryAlertMinimized));
  };

  // Only show the progress alert when on the settings/recovery page
  // Hide it in the library since the recovery card shows progress there
  const isOnRecoverySettingsPage =
    location.pathname.includes('/settings') &&
    location.pathname.includes('recovery');

  if (
    alertClosed ||
    activeRecoveries.length === 0 ||
    !isOnRecoverySettingsPage
  ) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-[9999] w-full max-w-[90%] sm:max-w-[450px]">
      <Alert className="relative items-start bg-background border shadow-lg">
        <div className="absolute right-2 top-2 flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleMinimize}
            className="h-6 w-6 p-0"
          >
            {isRecoveryAlertMinimized ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-6 w-6 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-start w-full pr-16">
          <div className="flex-1 w-full">
            <AlertTitle className={isRecoveryAlertMinimized ? 'mb-0' : 'mb-4'}>
              File Recovery Status ({activeRecoveries.length})
            </AlertTitle>
            <div className="max-h-[200px] overflow-auto">
              {!isRecoveryAlertMinimized &&
                activeRecoveries.map(([fileId, state]) => (
                  <div key={fileId} className="mb-4 last:mb-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-muted-foreground flex-1 overflow-hidden text-ellipsis whitespace-nowrap max-w-[calc(100%-30px)]">
                        {fileId}
                      </p>
                      <div className="flex justify-center relative">
                        {state.status === 'success' && (
                          <CheckCircle className="text-green-500 h-5 w-5" />
                        )}
                        {state.status === 'error' && (
                          <AlertCircle className="text-red-500 h-5 w-5" />
                        )}
                        {state.status === 'ongoing' && (
                          <span className="text-sm text-muted-foreground">
                            {Math.round(state.progress || 0)}%
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </Alert>
    </div>
  );
};

export default RecoveryProgressAlert;
